import { FC, useState, useEffect } from "react";
import { logoutApi } from "../../service/user";
import { getNoticeListApi, deleteNoticeApi, markNoticeAsReadApi, approveNoticeApi, getUnreadNotifyCountApi } from "../../service/notice";
import { getGroupMembersByGroupNoApi } from "../../service/profile";
import { applyJoinDeptApi, getDeptListSimpleApi, getDeptApi, applyOutDeptApi } from "../../service/team";
import { getMenuPermissionsApi } from "../../service/menu";
import { AlertMessage } from "../common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { authManager } from "../../utils/authManager";
import { InitService } from "../../utils/initService";
import { PermissionGuard } from "../common/PermissionGuard";
import { globalStore } from "../../utils/globalStore";
import { Button, Modal, Pagination } from "antd";
import { TeamModal } from "../common/TeamModal";
import { getLanguageName } from "../../utils/languageUtils";
import { PointsDetailModal } from "../common/PointsDetailModal";
import { APP_NAME, SHANHAI_LOGO } from "../../config/constants";
import { getPointsSummaryApi } from "../../service/points";
import { UserRoleManager } from "../../utils/userRole";
import { useUserRole } from "../../hooks/useUserRole";
import "./NavBar.scss";

interface Props {}

interface UserInfo {
  username: string;
  nickname?: string;
  email: string | null;
  phone: string | null;
  status: boolean;
  id: number;
  groups?: any[]; // 用户所属团队信息
  roles: Array<{
    role_name: string;
    role_key: string;
    create_by: string;
    create_time: string;
    update_by: string;
    update_time: string;
    remark: any;
    version: any;
  }>;
  notices: any[];
}

interface Notification {
  id: number;
  content: string;
  date: string;
  type: 'review' | 'approval';
  hasActions?: boolean;
}

export const NavBar: FC<Props> = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { isOperator, isAssessor } = useUserRole();

  // 检查团队操作权限的函数
  const checkTeamPermission = (permission: string): boolean => {
    // 首先检查 permissions 数组（从 getMenuPermissionsApi 接口响应的 data.permissions 中获取）
    const permissions = globalStore.getPermissions();
    if (permissions && permissions.length > 0 && permissions.includes(permission)) {
      return true;
    }

    // 如果 permissions 数组中没有找到，再检查 menus 中的 meta.perms
    const menuList = globalStore.getMenuList();
    
    if (!menuList || menuList.length === 0) {
      return false;
    }

    // 递归查找菜单权限
    const findMenuPermission = (menus: any[], targetPermission: string): boolean => {
      for (const menu of menus) {
        // 检查当前菜单的权限 - 只检查 meta.perms 字段
        if (menu.meta?.perms === targetPermission) {
          return true;
        }
        // 递归检查子菜单
        if (menu.children && menu.children.length > 0) {
          if (findMenuPermission(menu.children, targetPermission)) {
            return true;
          }
        }
      }
      return false;
    };

    return findMenuPermission(menuList, permission);
  };

  // 检查用户角色是否为操作者（显式排除管理员和审核者）
  const isOperatorOnly = (): boolean => {
    // 1. 获取所有角色标识并统一转小写
    const roleKeys = userInfo?.roles?.map((role: any) => 
      (typeof role === 'string' ? role : role.role_key || role.code || '').toLowerCase()
    ) || [];

    // 2. 显式排除管理员：如果用户是管理员或超级管理员，直接返回 false
    const isAdmin = roleKeys.some((key: string) => 
      key === 'admin' || key === 'super_admin' || key === 'administrator'
    );
    if (isAdmin) {
      return false;
    }

    // 3. 显式排除审核者：如果用户是审核者，直接返回 false
    const isAssessorRole = roleKeys.some((key: string) => 
      key === 'assessor' || key === 'reviewer'
    );
    if (isAssessorRole) {
      return false;
    }

    // 4. 使用 useUserRole hook 检查角色（作为主要判断）
    // 只检查是否为操作者，不检查审核者
    if (isOperator) {
      // 再次确认不是管理员和审核者（双重保险）
      if (isAdmin || isAssessorRole) {
        return false;
      }
      return true;
    }
    
    // 5. 备用检查：仅允许纯粹的操作者
    return roleKeys.some((key: string) => key === 'operator');
  };

  // 检查加入团队权限（使用后端实际返回的权限字符串）
  const hasAddGroupPermission = (): boolean => {
    // 后端返回的权限是 dept:apply:create
    return checkTeamPermission('dept:apply:create');
  };

  // 检查退出团队权限（使用后端实际返回的权限字符串）
  const hasOutGroupPermission = (): boolean => {
    // 后端返回的权限是 dept:apply:create（加入和退出使用同一个权限）
    return checkTeamPermission('dept:apply:create');
  };

  // 判断应该显示哪个团队操作按钮
  const getTeamActionButton = () => {
    // 第一步：先检查角色，必须是操作者（排除管理员和审核者）
    if (!isOperatorOnly()) {
      return null;
    }
    
    // 第二步：在满足角色的前提下，检查权限
    const hasAdd = hasAddGroupPermission();
    const hasOut = hasOutGroupPermission();
    
    // 根据用户信息中的 dept 或 groups 字段判断是否已加入团队
    // 优先检查 dept 字段（从 getMenuPermissionsApi 返回），如果没有则检查 groups 字段
    // 同时检查 globalStore 中的 dept 信息，确保在初始化时也能正确判断
    // 注意：团队id为4是临时团队（退出团队后的用户临时存在的地方），应视为不在团队中
    const storedUserInfo = globalStore.getUserInfo();
    const userDept = (userInfo as any)?.dept || (storedUserInfo as any)?.dept;
    const userGroups = userInfo?.groups || storedUserInfo?.groups;
    
    // 判断是否在团队中：
    // 1. 如果有 dept 且 dept.id !== 4，视为在团队中
    // 2. 如果没有 dept 但有 groups 且 groups 不为空，视为在团队中
    // 3. 如果 dept.id === 4，视为不在团队中（临时团队）
    const isInGroup = 
      (userDept && userDept.id !== 4) ||
      (!userDept && userGroups && userGroups.length > 0);
    
    // 如果用户已经在团队中且有退出团队权限，显示退出团队
    if (isInGroup && hasOut) {
      return {
        type: 'out',
        text: '退出团队',
        onClick: handleOutGroup
      };
    }
    
    // 如果用户不在团队中且有加入团队权限，显示加入团队
    if (!isInGroup && hasAdd) {
      return {
        type: 'add',
        text: '加入团队',
        onClick: handleAddGroup
      };
    }
    
    // 其他情况不显示团队操作按钮
    return null;
  };
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [noticeList, setNoticeList] = useState<NOTICE.NoticeListResponse | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(6);
  const [showPointsDetailModal, setShowPointsDetailModal] = useState(false);
  const [availablePoints, setAvailablePoints] = useState<number>(0);
  
  // 团队弹窗相关状态
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalType, setTeamModalType] = useState<'create' | 'join' | 'exit'>('create');
  const [teamModalLoading, setTeamModalLoading] = useState(false);
  const [currentTeamInfo, setCurrentTeamInfo] = useState<{
    teamId: string;
    auditorName: string;
  }>({ teamId: '', auditorName: '' });
  const { alertConfig, showAlert, hideAlert, showSuccess, showError, showWarning, showInfo } = useAlert();
  
  const location = useLocation();
  const navigate = useNavigate();

  // 单独获取未读通知数量，保证数值来自专门统计接口，且在失败时不会被错误清零
  const fetchUnreadCount = async (retry: number = 0) => {
    try {
      const res = await getUnreadNotifyCountApi();
      if (res && res.code === 0 && typeof res.data === "number") {
        setUnreadCount(res.data);
      } else if (retry < 2) {
        // 若本次未获取到有效数据，进行有限次数重试
        await fetchUnreadCount(retry + 1);
      }
    } catch (error) {
      console.error("获取未读通知数量失败:", error);
      // 失败时不清零已有的 unreadCount，只在可控次数内重试
      if (retry < 2) {
        await fetchUnreadCount(retry + 1);
      }
    }
  };

  useEffect(() => {
    // 从globalStore获取用户信息（统一使用持久化存储）
    const updateUserInfo = () => {
      const storedUserInfo = globalStore.getUserInfo();
      if (storedUserInfo) {
        setUserInfo(storedUserInfo);

        // 将后端返回的完整用户信息（包含 roles、permissions 等）
        // 同步到 UserRoleManager 的持久化中，供全局角色/权限判断使用
        UserRoleManager.updateUserRoleInfo(storedUserInfo as any);
      }
    };

    // 检查是否需要（重新）初始化菜单和权限：
    // 1. 首次进入（未初始化）
    // 2. 已初始化但本地没有用户信息或permissions为空（例如长时间登录后被清空）
    const token = localStorage.getItem("token");
    const storedUserInfo = globalStore.getUserInfo();
    const permissions = storedUserInfo?.permissions || [];
    const needInit =
      !!token &&
      (!InitService.isInitialized() || !storedUserInfo || permissions.length === 0);

    if (needInit) {
      InitService.initializeUserData()
        .then(() => {
          updateUserInfo();
        })
        .catch((error) => {
          console.error('初始化用户数据失败:', error);
        });
    } else {
      // 已有有效的用户信息，直接更新到本地状态
      updateUserInfo();
    }
    
    // 订阅 globalStore 的变化，以便在昵称修改后自动更新
    const unsubscribe = globalStore.subscribe(() => {
      updateUserInfo();
    });
    
    // 获取通知列表
    const fetchNoticeList = async () => {
      console.log('开始获取通知列表...');
      try {
        const response = await getNoticeListApi({ 
          unread_only: false,
          page: currentPage,
          page_size: pageSize
        });
        console.log('通知列表获取成功:', response);
        setNoticeList(response);
        // 未读数量统一交给专门的统计接口处理
      } catch (error) {
        console.error('获取通知列表失败:', error);
        // 如果接口调用失败，设置默认值
        setNoticeList(null);
      }
    };

    fetchNoticeList();
    fetchUnreadCount();
    
    // 清理函数：取消订阅
    return () => {
      unsubscribe();
    };
  }, []);

  // 获取积分汇总信息，用于头部导航栏展示，并监听全局积分更新事件
  useEffect(() => {
    const fetchPoints = async () => {
      try {
        const res = await getPointsSummaryApi();
        if (res.code === 0 && res.data) {
          setAvailablePoints(res.data.availablePoints ?? 0);
        }
      } catch (error) {
        console.error("获取积分信息失败:", error);
      }
    };

    fetchPoints();

    const handler = () => {
      fetchPoints();
    };
    window.addEventListener("points:refresh", handler);

    return () => {
      window.removeEventListener("points:refresh", handler);
    };
  }, []);

  const handleLogout = async () => {
    try {
      // 调用退出登录API
      await logoutApi();
      
      // 显示成功提示
      showSuccess('退出登录成功');
      
      // 清除本地存储
      localStorage.removeItem("token");
      
      // 清除全局状态（包括localStorage中的用户信息）
      InitService.clearUserData();
      
      // 1.5秒后跳转到登录页（与AlertMessage显示时间一致）
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
      
    } catch (error: any) {
      // 即使API调用失败，也要清除本地存储并跳转
      localStorage.removeItem("token");
      
      // 清除全局状态（包括localStorage中的用户信息）
      InitService.clearUserData();
      
      window.location.href = "/login";
    }
  };

  // 处理加入团队
  const handleAddGroup = () => {
    setTeamModalType('join');
    setShowTeamModal(true);
  };

  // 获取当前用户的团队信息
  const getCurrentTeamInfo = async () => {
    try {
      // 优先从 dept 中获取团队信息
      const storedUserInfo = globalStore.getUserInfo();
      const userDept = (userInfo as any)?.dept || (storedUserInfo as any)?.dept;

      const getReviewerNamesFromDept = (deptLike: any): string => {
        const reviewers = deptLike?.reviewers || deptLike?.dept?.reviewers || [];
        if (!Array.isArray(reviewers) || reviewers.length === 0) return '';
        return reviewers
          .map((r: any) => r?.nickname)
          .filter((n: any): n is string => typeof n === 'string' && !!n.trim())
          .join(',');
      };
      
      if (!userDept || !userDept.id || userDept.id === 4) {
        // 如果没有 dept 信息或 id 为 4（临时团队），尝试从 groups 获取
        const userGroups = userInfo?.groups || storedUserInfo?.groups;
        if (!userGroups || userGroups.length === 0) {
          console.log('用户未加入任何团队');
          return { teamId: '', auditorName: '' };
        }

        // 获取第一个团队的编号（假设用户只属于一个团队）
        const teamId = userGroups[0]?.group_no || userGroups[0]?.team_id || '';
        if (!teamId) {
          console.log('无法获取团队编号');
          return { teamId: '', auditorName: '' };
        }

        // 获取团队成员信息，查找审核者
        const members = await getGroupMembersByGroupNoApi(teamId);
        const assessors = members.filter(member => member.role === 'assessor');
        
        // 获取审核者名称
        const auditorName = assessors.length > 0 
          ? assessors.map(assessor => assessor.username).join(',')
          : '管理员';

        return { teamId, auditorName };
      }

      // 从 dept 中获取团队名称
      const teamName = userDept.name || '';
      
      // 使用 /system/auth/get-permission-info 接口获取审核者信息
      let auditorName = '管理员'; // 默认值
      try {
        const permissionResponse = await getMenuPermissionsApi();
        if (permissionResponse?.code === 0 && permissionResponse?.data?.dept) {
          const deptData = permissionResponse.data.dept;
          
          // 优先展示 reviewers（多个用逗号分隔），否则回退到 leaderNickname
          const reviewerNames = getReviewerNamesFromDept(deptData);

          if (reviewerNames) {
            auditorName = reviewerNames;
          } else if (deptData.leaderNickname) {
            auditorName = deptData.leaderNickname;
          }
        }
      } catch (error) {
        console.error('获取权限信息失败:', error);
      }

      return { teamId: teamName, auditorName };
    } catch (error) {
      console.error('获取团队信息失败:', error);
      return { teamId: '', auditorName: '' };
    }
  };

  // 处理退出团队
  const handleOutGroup = async () => {
    setTeamModalType('exit');
    
    // 获取当前团队信息
    const teamInfo = await getCurrentTeamInfo();
    setCurrentTeamInfo(teamInfo);
    
    setShowTeamModal(true);
  };


  // 处理团队弹窗取消
  const handleTeamModalCancel = () => {
    setShowTeamModal(false);
    setTeamModalLoading(false);
  };

  // 更新用户团队状态
  const updateUserTeamStatus = async () => {
    try {
      await InitService.initializeUserData();
      console.log('用户信息已更新，团队状态已同步');
      
      // 更新本地状态，确保UI立即反映变化
      const updatedUserInfo = InitService.getUserInfo();
      if (updatedUserInfo) {
        setUserInfo(updatedUserInfo);
        console.log('本地用户状态已更新:', updatedUserInfo.groups);
      }
    } catch (refreshError) {
      console.error('刷新用户信息失败:', refreshError);
      throw refreshError;
    }
  };

  // 处理团队弹窗确认
  const handleTeamModalConfirm = async (teamId?: string, teamName?: string) => {
    setTeamModalLoading(true);
    try {
      if (teamModalType === 'join') {
        // 处理申请加入团队逻辑
        if (!teamId) {
          showError('请输入团队名称');
          setTeamModalLoading(false);
          return;
        }
        
        try {
          // 根据团队名称查找部门ID
          // 使用精简接口获取所有部门列表
          const deptListResponse = await getDeptListSimpleApi();
          const deptList = Array.isArray(deptListResponse?.data) ? deptListResponse.data : [];
          
          // 根据团队名称（teamId）查找对应的部门
          const matchedDept = deptList.find(dept => 
            dept.name === teamId || dept.id.toString() === teamId
          );
          
          if (!matchedDept) {
            showError('未找到该团队，请检查团队名称是否正确');
            setTeamModalLoading(false);
            return;
          }
          
          const deptId = matchedDept.id;
          
          // 调用申请加入部门的接口
          const response = await applyJoinDeptApi(deptId);
          
          if (response && response.code === 0) {
            showSuccess('申请已提交，等待审核');
            // 关闭弹窗
            setShowTeamModal(false);
            // 刷新用户信息
            await updateUserTeamStatus();
          } else {
            showError(response?.msg || '申请失败，请重试');
          }
          
        } catch (error: any) {
          console.error('申请加入团队失败:', error);
          let errorMessage = '申请失败，请重试';
          if (error?.response?.detail) {
            errorMessage = error.response.detail;
          } else if (error?.response?.message) {
            errorMessage = error.response.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }
          showError(errorMessage);
        } finally {
          setTeamModalLoading(false);
        }
      } else if (teamModalType === 'exit') {
        // 处理申请退出团队逻辑
        try {
          // 调用申请退出部门的接口
          const response = await applyOutDeptApi();
          
          if (response && response.code === 0) {
            showSuccess('申请已提交，等待审核');
            // 关闭弹窗
            setShowTeamModal(false);
            // 刷新用户信息
            await updateUserTeamStatus();
          } else {
            showError(response?.msg || '申请失败，请重试');
          }
        } catch (error: any) {
          console.error('申请退出团队失败:', error);
          let errorMessage = '申请失败，请重试';
          if (error?.response?.detail) {
            errorMessage = error.response.detail;
          } else if (error?.response?.message) {
            errorMessage = error.response.message;
          } else if (error?.message) {
            errorMessage = error.message;
          }
          showError(errorMessage);
        } finally {
          setTeamModalLoading(false);
        }
      } else if (teamModalType === 'create') {
        // 处理创建团队逻辑
        console.log('创建团队:', teamName);
        // 这里可以调用创建团队的API
        await new Promise(resolve => setTimeout(resolve, 1000)); // 模拟API调用
        showSuccess('成功创建团队');
      }
      setShowTeamModal(false);
    } catch (error: any) {
      console.error('团队操作失败:', error);
      showError(error.message || '操作失败，请重试');
    } finally {
      setTeamModalLoading(false);
    }
  };

  // 获取显示的用户名（优先显示 nickname，如果没有则显示 username）
  const displayName = userInfo?.nickname || userInfo?.username || '用户';
  
  // 调试信息
  console.log('NavBar用户信息调试:', {
    userInfo,
    username: userInfo?.username,
    displayName
  });

  // 处理创建新剧集按钮点击
  const handleCreateNewEpisode = () => {
    navigate('/myWork');
  };

  // 处理通知点击
  const handleNotificationClick = () => {
    setCurrentPage(1); // 重置到第一页
    setShowNotificationModal(true);
  };

  // 处理积分点击
  const handlePointsClick = () => {
    setShowPointsDetailModal(true);
  };

  // 处理通知删除
  const handleDeleteNotification = async (id: number) => {
    try {
      // 调用真正的删除接口
      await deleteNoticeApi(id);
      
      // 重新获取当前页的数据
      const response = await getNoticeListApi({ 
        unread_only: false,
        page: currentPage,
        page_size: pageSize
      });
      setNoticeList(response);
      // 使用未读数量统计接口刷新红点数量
      fetchUnreadCount();
      
      showSuccess('通知已删除');
    } catch (error) {
      console.error('删除通知失败:', error);
      showError('删除通知失败，请重试');
    }
  };

  // 处理审核操作（通过 / 不通过）
  const handleApproval = async (id: number, action: 'approve' | 'reject') => {
    try {
      const approve = action === 'approve';
      const response = await approveNoticeApi({
        id,
        approve,
      });

      if (response.code === 0) {
        // 根据操作类型显示不同提示
        showSuccess(approve ? '已通过审核' : '已驳回');
      } else {
        const msg = response.msg || '处理失败，请重试';
        showError(msg);
        return;
      }

      // 重新获取当前页的数据
      const updatedResponse = await getNoticeListApi({ 
        unread_only: false,
        page: currentPage,
        page_size: pageSize
      });
      setNoticeList(updatedResponse);
      // 使用未读数量统计接口刷新红点数量
      fetchUnreadCount();
    } catch (error: any) {
      console.error('审核操作失败:', error);
      // 处理不同类型的错误
      let errorMessage = '处理失败，请重试';
      
      // 检查是否有响应数据中的detail / message / info
      if (error?.response?.detail) {
        errorMessage = error.response.detail;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.response?.info) {
        errorMessage = error.response.info;
      } else if (error?.status) {
        if (error.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试';
        } else if (error.status === 404) {
          errorMessage = '请求的资源不存在';
        } else if (error.status === 403) {
          errorMessage = '没有权限执行此操作';
        } else if (error.status === 401) {
          errorMessage = '登录验证失效，请重新登录';
        } else {
          errorMessage = `请求失败 (状态码: ${error.status})`;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    }
  };

  // 处理标记为已读操作
  const handleMarkAsRead = async (id: number) => {
    try {
      const response = await markNoticeAsReadApi({ id });
      
      if (response.code === 0 && response.data) {
        showSuccess('已标记为已读');
      } else {
        const msg = response.msg || '操作失败，请稍后重试';
        showError(msg);
        return;
      }
      
      // 重新获取当前页的数据
      const updatedResponse = await getNoticeListApi({ 
        unread_only: false,
        page: currentPage,
        page_size: pageSize
      });
      setNoticeList(updatedResponse);
      // 使用未读数量统计接口刷新红点数量
      fetchUnreadCount();
    } catch (error: any) {
      console.error('标记已读失败:', error);
      
      // 处理不同类型的错误
      let errorMessage = '操作失败，请重试';
      
      // 检查是否有响应数据中的detail
      if (error?.response?.detail) {
        errorMessage = error.response.detail;
      }
      // 检查是否有响应数据中的message
      else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      // 检查是否有响应数据中的info
      else if (error?.response?.info) {
        errorMessage = error.response.info;
      }
      // 检查是否有状态码信息
      else if (error?.status) {
        if (error.status === 500) {
          errorMessage = '服务器内部错误，请稍后重试';
        } else if (error.status === 404) {
          errorMessage = '请求的资源不存在';
        } else if (error.status === 403) {
          errorMessage = '没有权限执行此操作';
        } else if (error.status === 401) {
          errorMessage = '登录验证失效，请重新登录';
        } else {
          errorMessage = `请求失败 (状态码: ${error.status})`;
        }
      }
      // 检查是否有错误消息
      else if (error?.message) {
        errorMessage = error.message;
      }
      
      showError(errorMessage);
    }
  };

  // 处理分页变化
  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    // 重新获取对应页面的数据
    try {
      const response = await getNoticeListApi({ 
        unread_only: false,
        page: page,
        page_size: pageSize
      });
      setNoticeList(response);
    } catch (error) {
      console.error('获取通知列表失败:', error);
      showError('获取通知列表失败，请重试');
    }
  };


  return (
    <>
      {/* 退出登录成功提示 */}
      <AlertMessage 
        config={alertConfig}
        onClose={hideAlert}
        zIndex={ALERT_Z_INDEX.NAVBAR}
      />

      <header className="navbar">
        <div className="navbar-container">
          {/* 左侧品牌和菜单导航 */}
          <div className="navbar-left">
            <div className="navbar-brand">
              <div className="logo-placeholder">
                <img src={SHANHAI_LOGO} alt={APP_NAME} className="logo-img" />
              </div>
              <h1 className="navbar-title">{APP_NAME}</h1>
            </div>
            
            {/* 菜单导航 */}
            <div className="navbar-menu">
              <NavLink 
                to="/home" 
                className={({ isActive }) => isActive ? 'nav-btn nav-btn-active' : 'nav-label'}
              >
                我的剧集
              </NavLink>
              <NavLink 
                to="/songs" 
                className={({ isActive }) => isActive ? 'nav-btn nav-btn-active' : 'nav-label'}
              >
                音色库
              </NavLink>
             
            </div>
          </div>

          {/* 右侧用户信息和操作 */}
          <div className="navbar-right">
            {/* 测试Token按钮 */}
            {/* <button 
              className="test-token-btn"
              onClick={() => authManager.handleTokenExpired()}
              title="测试Token过期处理"
            >
              <i className="fas fa-key"></i>
              测试Token
            </button> */}

            {/* 积分显示  积分注释*/}
            {/* <div className="points-display" onClick={handlePointsClick}>
              <img src="/img/jf_icon.png" alt="积分" className="points-icon" />
              <span className="points-value">{availablePoints}</span>
            </div> */}

            {/* 通知图标 */}
            <div className="notification-icon" onClick={handleNotificationClick}>
              <i className="fas fa-envelope"></i>
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>

            <div 
              className="user-dropdown-container"
              onMouseEnter={() => setShowUserDropdown(true)}
              onMouseLeave={() => setShowUserDropdown(false)}
            >
              <div className="username">
                <span className="user-name">{displayName}</span>
                <i className="fas fa-chevron-down dropdown-arrow"></i>
              </div>
              
              {showUserDropdown && (
                <div className="user-dropdown">
                  <div className="dropdown-item" onClick={() => navigate('/profile')}>
                    <span>个人中心</span>
                  </div>
                  
                  {/* 团队操作按钮 - 只显示一个 */}
                  {(() => {
                    const teamAction = getTeamActionButton();
                    return teamAction ? (
                      <>
                        <div className="dropdown-separator"></div>
                        <div className="dropdown-item" onClick={teamAction.onClick}>
                          <span>{teamAction.text}</span>
                        </div>
                      </>
                    ) : null;
                  })()}
                  
                  <div className="dropdown-separator"></div>
                  <div className="dropdown-item logout-item" onClick={handleLogout}>
                    <span>退出登录</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* 通知弹窗 */}
      <Modal
        title={
          <div className="notification-modal-title">
            <span>信息提醒</span>
            <span className="notification-count">(共{noticeList?.total || 0}条通知)</span>
          </div>
        }
        open={showNotificationModal}
        onCancel={() => {
          setShowNotificationModal(false);
          setCurrentPage(1); // 关闭时重置页码
        }}
        footer={null}
        width={925}
        className="notification-modal"
      >
        <div className="notification-content">
          {(() => {
            const allNotices = noticeList?.notices || [];
            const start = (currentPage - 1) * pageSize;
            const end = start + pageSize;
            const pagedNotices = allNotices.slice(start, end);
            return (pagedNotices.length > 0 ? pagedNotices : allNotices).map((notice) => {
              // 判断是否需要置灰
              // 业务提醒/系统公告（notice_type: "2"）：通过 read_status === "1" 判断已读
              // 审核请求（notice_type: "3"）：通过 process_status !== "0" 且 process_status !== "3" 判断已处理
              // processStatus: 0=待处理, 1=已同意, 2=已拒绝, 3=无需处理
              const shouldBeGrayed = notice.notice.notice_type === "2" 
                ? notice.read_status === "1"
                : notice.notice.notice_type === "3"
                ? notice.process_status !== "0" && notice.process_status !== "3" // 已处理（已同意/已拒绝），但无需处理的不置灰
                : false;
              
              return (
            <div 
              key={notice.id} 
              className={`notification-item ${shouldBeGrayed ? "read" : ""}`}
            >
              {/* 显示NEW徽章：
                  - 业务提醒/系统公告：read_status === "0"（未读）
                  - 审核请求：process_status === "0"（待处理） */}
              {((notice.notice.notice_type === "2" && notice.read_status === "0") ||
                (notice.notice.notice_type === "3" && notice.process_status === "0")) && (
                <img 
                  src="/img/NEW.png" 
                  alt="NEW" 
                  className="new-badge"
                />
              )}
              <div className="notification-text">
                <span className="bullet">•</span>
                {(() => {
                  const rawContent = notice.notice.notice_content || "";
                  const targetLangCode =
                    notice.notice_object?.target_lang ||
                    notice.notice_object?.targetLang ||
                    notice.notice_object?.lang ||
                    "";

                  if (!targetLangCode) {
                    return <span className="content">{rawContent}</span>;
                  }

                  const langName = getLanguageName(targetLangCode);
                  const replaced = rawContent.replace(targetLangCode, langName);

                  return <span className="content">{replaced}</span>;
                })()}
              </div>
              <div className="notification-actions">
                <span className="notification-date">
                  {new Date(notice.create_time).toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </span>
                {/* 显示操作按钮：
                    - 业务提醒/系统公告（notice_type: "2"）：read_status === "0"（未读）时显示"已读"按钮
                    - 审核请求（notice_type: "3"）：process_status === "0"（待处理）时显示"通过"和"不通过"按钮 */}
                {((notice.notice.notice_type === "2" && notice.read_status === "0") ||
                  (notice.notice.notice_type === "3" && notice.process_status === "0")) && (
                  <div className="action-buttons">
                    {notice.notice.notice_type === "3" && (
                      <>
                        <Button 
                          size="small" 
                          className="approve-btn"
                          onClick={() => handleApproval(notice.id, 'approve')}
                        >
                          通过
                        </Button>
                        <Button 
                          size="small" 
                          className="reject-btn"
                          onClick={() => handleApproval(notice.id, 'reject')}
                        >
                          不通过
                        </Button>
                      </>
                    )}
                    {notice.notice.notice_type === "2" && (
                      <Button 
                        size="small" 
                        className="read-btn"
                        onClick={() => handleMarkAsRead(notice.id)}
                      >
                        已读
                      </Button>
                    )}
                  </div>
                )}
                <i 
                  className="fas fa-trash delete-icon" 
                  onClick={() => handleDeleteNotification(notice.id)}
                ></i>
              </div>
            </div>
              );
            })
          })() || (
            <div className="no-notifications">
              <p>暂无通知</p>
            </div>
          )}
        </div>
        
        <div className="notification-pagination">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={noticeList?.total || noticeList?.notices?.length || 0}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
            simple
          />
        </div>
      </Modal>

      {/* 团队管理弹窗 */}
      <TeamModal
        visible={showTeamModal}
        type={teamModalType}
        onCancel={handleTeamModalCancel}
        onConfirm={handleTeamModalConfirm}
        loading={teamModalLoading}
        currentTeamId={currentTeamInfo.teamId}
        auditorName={currentTeamInfo.auditorName}
      />

      {/* 积分明细弹窗 */}
      <PointsDetailModal
        visible={showPointsDetailModal}
        onClose={() => setShowPointsDetailModal(false)}
      />

    </>
  );
};